import { useLazyQuery } from '@apollo/client';
import { IonSkeletonText } from '@ionic/react';
import { isSameDay, startOfMonth } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Select, { components, GroupBase } from 'react-select';
import { InputSpecificProps } from 'react-select/dist/declarations/src/components/Input';

import ExpertAvailabilityCalendarForExperts from '@components/expertSection/schedule/ExpertAvailabilityCalendarForExperts';
import ExpertAvailabilityOption from '@components/expertSection/schedule/ExpertAvailabilityOption';
import styles from '@components/expertSection/schedule/ScheduleSession.module.css';
import Checkbox from '@components/fieldsNative/Checkbox';
import BodyCopy from '@components/typography/BodyCopy';
import useNow from '@hooks/useNow';
import {
  ConsultAvailabilityFragment as ConsultAvailability,
  ConsultDurationEnum,
  GetConsultAvailabilityDaysDocument,
  GetConsultAvailabilityDaysQuery,
  GetConsultAvailabilityDaysQueryVariables,
  GetJourneyWithExpertAndConsultsQuery
} from '@typing/Generated';
import { scheduleSelectStyles } from '@utils/formUtils';
import { primaryExpertRole, sortByTimestamp } from '@utils/modelUtils';

type Journey = GetJourneyWithExpertAndConsultsQuery['journey'];

type Option = {
  label: string;
  value: ConsultAvailability;
};

type Props = {
  availabilitySelected: ConsultAvailability | null;
  duration: ConsultDurationEnum | null;
  journey: Journey;
  setAvailabilitySelected: (availability: ConsultAvailability | null) => void;
};

const Input = ({ ...props }) => (
  <components.Input {...(props as InputSpecificProps<unknown, boolean, GroupBase<unknown>>)} data-lpignore="true" />
);

const ExpertAvailabilityPickerForExperts = ({
  availabilitySelected,
  duration,
  journey,
  setAvailabilitySelected
}: Props) => {
  const now = useNow();

  const [nonPrimaryExperts, setNonPrimaryExperts] = useState<boolean>(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [currentStartOfMonth, setCurrentStartOfMonth] = useState<Date>(startOfMonth(now));

  const [getPrimaryAvailabilityDays, getPrimaryAvailabilityDaysQuery] = useLazyQuery<
    GetConsultAvailabilityDaysQuery,
    GetConsultAvailabilityDaysQueryVariables
  >(GetConsultAvailabilityDaysDocument, {
    notifyOnNetworkStatusChange: true
  });

  const [getOtherAvailabilityDays, getOtherAvailabilityDaysQuery] = useLazyQuery<
    GetConsultAvailabilityDaysQuery,
    GetConsultAvailabilityDaysQueryVariables
  >(GetConsultAvailabilityDaysDocument, {
    notifyOnNetworkStatusChange: true
  });

  const generateOption = useCallback(
    (
      journey: Journey,
      option: GetConsultAvailabilityDaysQuery['consultAvailabilityDays'][number]['availabilities'][number]
    ) => ({
      label: <ExpertAvailabilityOption journey={journey} option={option} />,
      value: option
    }),
    []
  );

  const endTime = useMemo(
    () => new Date(currentStartOfMonth.getFullYear(), currentStartOfMonth.getMonth() + 1, 2, 0, 0, 0),
    [currentStartOfMonth]
  );

  const primaryExpert = useMemo(() => primaryExpertRole(journey.expertRoles)?.expert, [journey.expertRoles]);

  const availabilityOptions = useMemo(() => {
    const selectedPrimaryAvailabilityDay =
      selectedDay &&
      getPrimaryAvailabilityDaysQuery.data?.consultAvailabilityDays.find(day => isSameDay(selectedDay, day.date));
    const selectedOtherAvailabilityDay =
      selectedDay &&
      getOtherAvailabilityDaysQuery.data?.consultAvailabilityDays.find(day => isSameDay(selectedDay, day.date));
    const primaryAvailabilities =
      selectedPrimaryAvailabilityDay?.availabilities.map(option => generateOption(journey, option)) ?? [];
    const otherAvailabilities =
      selectedOtherAvailabilityDay?.availabilities.map(option => generateOption(journey, option)) ?? [];
    return sortByTimestamp(
      nonPrimaryExperts ? primaryAvailabilities.concat(otherAvailabilities) : primaryAvailabilities,
      consultAvailability => consultAvailability.value.startTime
    ).reverse();
  }, [
    generateOption,
    getOtherAvailabilityDaysQuery.data?.consultAvailabilityDays,
    getPrimaryAvailabilityDaysQuery.data?.consultAvailabilityDays,
    journey,
    nonPrimaryExperts,
    selectedDay
  ]);

  const consultAvailabilityDays = useMemo(
    () =>
      getPrimaryAvailabilityDaysQuery.data?.consultAvailabilityDays.concat(
        getOtherAvailabilityDaysQuery.data?.consultAvailabilityDays ?? []
      ),
    [
      getOtherAvailabilityDaysQuery.data?.consultAvailabilityDays,
      getPrimaryAvailabilityDaysQuery.data?.consultAvailabilityDays
    ]
  );

  const isLoading = useMemo(
    () =>
      (!nonPrimaryExperts && getPrimaryAvailabilityDaysQuery.loading) ||
      (nonPrimaryExperts && (getPrimaryAvailabilityDaysQuery.loading || getOtherAvailabilityDaysQuery.loading)),
    [getOtherAvailabilityDaysQuery.loading, getPrimaryAvailabilityDaysQuery.loading, nonPrimaryExperts]
  );

  useEffect(() => {
    if (primaryExpert && duration && !nonPrimaryExperts) {
      getPrimaryAvailabilityDays({
        variables: {
          duration,
          endTime,
          journeyId: journey.id,
          nonPrimaryExperts: false,
          startTime: currentStartOfMonth
        }
      });
    }
  }, [journey, getPrimaryAvailabilityDays, primaryExpert, nonPrimaryExperts, endTime, currentStartOfMonth, duration]);

  useEffect(() => {
    if (primaryExpert && duration && nonPrimaryExperts) {
      getOtherAvailabilityDays({
        variables: {
          duration,
          endTime,
          journeyId: journey.id,
          nonPrimaryExperts: true,
          startTime: currentStartOfMonth
        }
      });
    }
  }, [journey, getOtherAvailabilityDays, primaryExpert, nonPrimaryExperts, endTime, currentStartOfMonth, duration]);

  useEffect(() => {
    setAvailabilitySelected(null);
  }, [nonPrimaryExperts, setAvailabilitySelected]);

  return (
    <>
      <ExpertAvailabilityCalendarForExperts
        consultAvailabilityDays={consultAvailabilityDays}
        currentStartOfMonth={currentStartOfMonth}
        duration={duration}
        selectedDay={selectedDay}
        setAvailabilitySelected={setAvailabilitySelected}
        setCurrentStartOfMonth={setCurrentStartOfMonth}
        setSelectedDay={setSelectedDay}
      />
      <div className={styles.tinySpacing}>
        <Checkbox checked={nonPrimaryExperts} error={null} name="nonPrimaryExperts" setValue={setNonPrimaryExperts}>
          <BodyCopy color="neutral500">
            <FormattedMessage id="pages.consults.selectTime.searchAnyAvailable" />
          </BodyCopy>
        </Checkbox>
      </div>
      <div className={styles.label}>
        <BodyCopy color="primary300">
          <FormattedMessage id="forms.consult.carePartnerTimeLabel" />
        </BodyCopy>
        <BodyCopy color="neutral500">
          <FormattedMessage id="forms.consult.memberTimeLabel" />
        </BodyCopy>
      </div>
      {isLoading ? (
        <IonSkeletonText animated />
      ) : availabilityOptions.length > 0 ? (
        <Select
          classNamePrefix="react-select"
          components={{ Input }}
          isDisabled={!selectedDay || !duration}
          isSearchable
          menuPlacement="auto"
          menuPortalTarget={document.body}
          onChange={option => {
            const properlyTypedOption = option as unknown as Option;
            setAvailabilitySelected(properlyTypedOption.value);
          }}
          openMenuOnFocus
          options={availabilityOptions}
          styles={scheduleSelectStyles}
          value={availabilityOptions.filter(option => availabilitySelected?.startTime === option.value.startTime)}
        />
      ) : (
        <BodyCopy color="neutral300">
          {!selectedDay || !duration ? (
            <FormattedMessage id="forms.consult.pickADay" />
          ) : (
            <FormattedMessage id="forms.consult.noAvailability" />
          )}
        </BodyCopy>
      )}
    </>
  );
};

export default ExpertAvailabilityPickerForExperts;
